/* eslint-disable arrow-body-style */
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import withdrawConfirm from '@/components/wallet/withdraw-confirm/withdraw-confirm.vue';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import availableBalance from '@/components/wallet/available-balance/available-balance.vue';
import { formMixin, validators } from '@/components/forms/mixin';
import documentUploadMixin from '@/mixins/documentUpload';
import contentLoaderMixin from '@/mixins/contentLoader';
import { roundDown } from '@/utils/helpers';
import notificationIcon from '@/components/notification-icon/notification-icon.vue';
import { MODALS } from '@/components/app/app-modals/app-modals';
import loaderMixin from '@/mixins/loader';
import infiniteLoading from 'vue-infinite-loading';

const TOO_MANY_RESULTS = 50;

export default {
  mixins: [
    contentLoaderMixin,
    documentUploadMixin,
    formMixin,
    loaderMixin,
  ],
  components: {
    alertBox,
    availableBalance,
    notificationIcon,
    withdrawConfirm,
    infiniteLoading,
  },
  data() {
    return {
      TOO_MANY_RESULTS,
      showConfirmation: false,
      showBankInfoForm: false,
      showWithdrawalAmountForm: false,
      showAddressSearch: false,
      isSearching: false,
      hasSearched: false,
      hasSavedAccount: false,
      showIbanValidationInfo: false,
      showIbanValidationForm: false,
      infiniteLoader: null,
      pickedSuggestion: null,
      accountId: null,
      beneficiaryInfo: {},
      error: '',
      query: '',
      disclaimer: '',
      beneficiaryFields: [],
      bankWithdrawAmounts: {
        grossWithdrawalAmount: 0,
        flatFeeAmount: 0,
        percentageFeeAmount: 0,
        totalFees: 0,
        netWithdrawalAmount: 0,
      },
      fieldsBankInfoForm: {
        searchBankName: '',
        searchCity: '',
        searchAddress: '',
        bankName: '',
        streetAddress: '',
        city: '',
        zipCode: '',
        region: '',
      },
      fieldsWithdrawalAmountForm: {
        amount: '',
      },
    };
  },
  validations() {
    if (this.showBankInfoForm) {
      let rules = {};
      this.beneficiaryFields.forEach((field) => {
        rules = {
          ...rules,
          [field.id]: validators.customPattern(field.regEx, field.errorMessage),
        };
      });
      return {
        fieldsBankInfoForm: rules,
      };
    }
    return {
      fieldsWithdrawalAmountForm: {
        amount: validators.withdrawalAmount(this.minWithdrawal, this.maxWithdrawal),
      },
    };
  },
  watch: {
    suggestions() {
      if (this.infiniteLoader) {
        this.infiniteLoader.loaded();
        if (!this.wireSearchResults.canLoadMore) {
          this.infiniteLoader.complete();
        }
      }
    },
  },
  computed: {
    ...mapState('wallet', ['wire', 'wireSearchResults']),
    ...mapState('user', ['userData']),
    beneficiaryFieldsJustFirst() {
      return this.beneficiaryFields.slice(0, 1);
    },
    beneficiaryFieldsWithoutFirst() {
      return this.beneficiaryFields.slice(1, this.beneficiaryFields.length);
    },
    suggestions() {
      return this.wireSearchResults.data === null ? [] : this.wireSearchResults.data;
    },
    suggestionsTotal() {
      return this.wireSearchResults.data === null ? 0 : this.wireSearchResults.totalCount;
    },
    amountPlaceholder() {
      return `Withdrawal Amount (Minimum $${this.minWithdrawal} USD)`;
    },
    minWithdrawal() {
      return this.wire ? this.wire.minimumWithdrawalAmount : 0;
    },
    maxWithdrawal() {
      return this.wire ? roundDown(this.wire.availableFunds) : 0;
    },
    isAmountFieldDisabled() {
      return this.minWithdrawal < 1 || this.maxWithdrawal < 1;
    },
    availableBalance() {
      return (this.wire && this.wire.availableFundsString) ? this.wire.availableFundsString : '$0.00';
    },
    restrictedFunds() {
      return (this.wire && this.wire.restrictedFundsString) ? this.wire.restrictedFundsString : '';
    },
    errorMessage() {
      return this.error;
    },
    hasErrorMessage() {
      return this.error;
    },
  },
  methods: {
    ...mapActions('wallet', [
      'getWithdrawWire', 'getWithdrawWireBankSearch',
      'postWithdrawWireBankInfo', 'postWithdrawWireConfirm',
      'resetWireSearchResults',
    ]),
    ...mapActions('popup', ['setCurrentSmallPopup']),
    goInfinite(infi) {
      this.infiniteLoader = infi;

      if (this.wireSearchResults.canLoadMore) {
        this.loadWireSearch();
      }
    },
    loadWireSearch({ reset = false } = {}) {
      this.isSearching = true;
      if (reset) {
        this.hasSearched = false;
      }
      this.getWithdrawWireBankSearch({ query: this.query })
        .then((data) => {
          if (data.totalResults > TOO_MANY_RESULTS) {
            this.showAddressSearch = true;
          }
          this.hasSearched = true;
          this.isSearching = false;
        })
        .catch(() => {
          this.isSearching = false;
          this.infiniteLoader.complete();
          this.infiniteLoader = null;
        });
    },
    reviewRequest() {
      if (this.formIsValid() && this.formDisable()) {
        const payload = {
          amount: this.fieldsWithdrawalAmountForm.amount,
          accountWireBeneficiaryId: this.beneficiaryInfo.accountWireBeneficiaryId,
        };
        this.postWithdrawWireConfirm(payload)
          .then((data) => {
            this.bankWithdrawAmounts = data;
            this.showConfirmation = true;
            this.loaderRemove(document.body);
            this.formEnable();
          })
          .catch((error) => {
            this.loaderRemove(document.body);
            this.formHandleErrors(error);
            this.formEnable();
          });
      }
    },
    confirmAccountInfo() {
      if (this.formIsValid() && this.formDisable()) {
        const payload = {};
        this.beneficiaryFields.forEach((field) => {
          payload[field.id] = this.fieldsBankInfoForm[field.id];
        });
        this.postWithdrawWireBankInfo(payload)
          .then((data) => {
            this.beneficiaryInfo = data.beneficiaryInfo;
            if (data.canWithdraw) {
              this.showWithdrawalAmountForm = true;
              this.showBankInfoForm = false;
            } else {
              this.showIbanValidationInfo = true;
              this.showIbanValidationForm = false;
              this.showAddressSearch = true;
            }
            this.loaderRemove(document.body);
            this.formEnable();
          })
          .catch((error) => {
            this.loaderRemove(document.body);
            this.formHandleErrors(error);
            this.formEnable();
          });
      }
    },
    confirmBankInfo() {
      if (this.formIsValid() && this.formDisable()) {
        const payload = {
          bankId: this.pickedSuggestion.id,
        };
        this.beneficiaryFields.forEach((field) => {
          payload[field.id] = this.fieldsBankInfoForm[field.id];
        });
        this.postWithdrawWireBankInfo(payload)
          .then((data) => {
            this.beneficiaryInfo = data.beneficiaryInfo;
            this.showWithdrawalAmountForm = true;
            this.showBankInfoForm = false;
            this.loaderRemove(document.body);
            this.formEnable();
          })
          .catch((error) => {
            this.loaderRemove(document.body);
            this.formHandleErrors(error);
            this.formEnable();
          });
      }
    },
    showSwiftModal() {
      this.setCurrentSmallPopup(MODALS.SMALL_SWIFT);
    },
    customFieldsClassModifiers(field) {
      return {
        column: true,
        'small-6': true,
        'bank-transfer--hidden-column': field.hidden,
      };
    },
    suggestionClassModifiers(suggestion) {
      return {
        'bank-transfer__suggestion': true,
        'bank-transfer__suggestion--not-valid': !suggestion.isValid,
      };
    },
    onSuggestionPick(suggestion) {
      if (!suggestion.isValid) return;

      this.hasSearched = false;
      this.fieldsBankInfoForm.bankName = suggestion.name;
      this.fieldsBankInfoForm.streetAddress = `${suggestion.address1} ${suggestion.address2}`;
      this.fieldsBankInfoForm.city = suggestion.city;
      this.fieldsBankInfoForm.region = suggestion.region;
      this.fieldsBankInfoForm.zipCode = suggestion.postalCode;
      this.fieldsBankInfoForm.swiftBicCode = suggestion.swiftCode;
      this.pickedSuggestion = suggestion;
      this.resetWireSearchResults();
    },
    onFieldInput(fieldId) {
      const f = this.beneficiaryFields.find(i => i.id === fieldId);
      const vField = this.$v.fieldsBankInfoForm[fieldId];
      vField.errorMessage = (f || {}).errorMessage || '';
      vField.$touch();
    },
    onSearch() {
      let query = `${this.fieldsBankInfoForm.searchCity} ${this.fieldsBankInfoForm.searchBankName}`;
      if (this.showAddressSearch) {
        query += ` ${this.fieldsBankInfoForm.searchAddress}`;
      }
      if (query && !this.isSearching) {
        this.resetWireSearchResults();
        this.query = query;
        this.loadWireSearch({ reset: true });
      }
    },
    onSuccess() {
      this.showConfirmation = false;
      this.checkWithdrawBankTransfer();
      this.$emit('success');
    },
    onEdit() {
      this.showConfirmation = false;
    },
    changeBankInformation() {
      this.resetWireSearchResults();
      this.showWithdrawalAmountForm = false;
      this.showBankInfoForm = true;
    },
    onCancel() {
      this.showWithdrawalAmountForm = true;
      this.showBankInfoForm = false;
    },
    checkWithdrawBankTransfer() {
      this.getWithdrawWire()
        .then((data) => {
          this.checkDocumentUploadFlags(data);
          this.disclaimer = data.disclaimer;
          this.beneficiaryInfo = data.beneficiaryInfo;
          this.beneficiaryFields = (data.beneficiaryFields || []).map((field) => {
            return {
              ...field,
              value: '',
            };
          });
          this.beneficiaryFields.forEach((field) => {
            this.fieldsBankInfoForm = {
              ...this.fieldsBankInfoForm,
              [field.id]: field.defaultValue || '',
            };
          });

          this.error = data.errorMessage;
          this.isComponentReady = true;
          this.hasSavedAccount = data.hasSavedAccount;
          this.showIbanValidationForm = data.isIbanEnabled && !data.hasSavedAccount;
          this.$nextTick(() => {
            if (data.hasSavedAccount) {
              this.showWithdrawalAmountForm = true;
            } else {
              this.showBankInfoForm = true;
            }
          });
        })
        .catch((error) => {
          this.isComponentReady = true;
          this.error = get(error, 'response.data.message', '') || get(error, 'response.data.errorMessage', '');
        });
    },
  },
  beforeDestroy() {
    this.resetWireSearchResults();
  },
  mounted() {
    this.checkWithdrawBankTransfer();
  },
};



// WEBPACK FOOTER //
// ./src/components/wallet/bank-transfer/bank-transfer.js